import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { RadioSelector } from '@/components/RadioSelector';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { FastestTag } from '@/feature/deposit/components/FastestTag';
import { TrustlyBrandingLogos } from '@/feature/kyc/components/TrustlyBrandingLogos';
import { useTrustlyKycStore } from '@/feature/kyc/hooks/use-trustly-kyc-store';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useIsFirstScreenInStack } from '@/hooks/use-is-first-screen-in-stack';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { IdpvSource } from '@/navigation/types';

enum WithdrawVerificationOption {
    TRUSTLY = 'trustly',
    IDPV = 'idpv',
}

/**
 * Choice screen shown when withdrawal verification is required and both the
 * Trustly and IDPV paths are available: Trustly (fastest, pre-selected) or
 * manual verification via IDPV.
 */
export const VerificationSelector = () => {
    // Trustly is the recommended path and starts pre-selected.
    const [selectedOption, setSelectedOption] = useState<WithdrawVerificationOption>(
        WithdrawVerificationOption.TRUSTLY
    );
    const { t } = useTranslation(['kyc', 'account']);
    const tSocialWallet = useSocialWalletTranslation('account');
    const navigation = useNavigation<KycStackNavigationProp<'VerificationSelector'>>();
    const isFirstScreenInStack = useIsFirstScreenInStack();

    useEffect(() => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_SELECTOR_ACCESSED);
    }, []);

    const handleTrustlySelection = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_SELECTED_TRUSTLY);
        setSelectedOption(WithdrawVerificationOption.TRUSTLY);
        useTrustlyKycStore.getState().setShouldProceedToWithdrawal(true);
        navigation.navigate('TrustlyId');
    };

    const handleIdpvSelection = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_SELECTED_IDPV);
        setSelectedOption(WithdrawVerificationOption.IDPV);
        navigation.navigate('KycScanId', { source: IdpvSource.WITHDRAW });
    };

    const trustlySelectorTitle = (
        <Row alignItems="center" gap="s8">
            <Text variant="titleLarge" color={ds.colorToken.text.primary}>
                {t('account:trustly_withdraw_option_title')}
            </Text>
            <FastestTag />
        </Row>
    );

    return (
        <Screen edges={['top', 'bottom']}>
            <ScreenNavBar title={t('kyc:verify_identity')} closeIconMode={isFirstScreenInStack ? 'close' : 'back'} />
            <ScrollView>
                <MaxWidthWrapper paddingHorizontal="s16">
                    <Box>
                        <Text variant="headlineMedium" mb="s6">
                            {t('kyc:choose_a_verification_option')}
                        </Text>
                        <Text color={ds.colorToken.text.secondary}>
                            {tSocialWallet('withdraw_verification_selector_description')}
                        </Text>
                        <SizedBox value={24} />
                        <RadioSelector
                            isSelected={selectedOption === WithdrawVerificationOption.TRUSTLY}
                            title={trustlySelectorTitle}
                            description={t('account:trustly_withdraw_option_description')}
                            testID="trustly-withdraw-verification-selector"
                            onPress={handleTrustlySelection}
                            footer={<TrustlyBrandingLogos />}
                        />
                        <SizedBox value={16} />
                        <RadioSelector
                            isSelected={selectedOption === WithdrawVerificationOption.IDPV}
                            title={t('kyc:idcomply_kyc_option_title')}
                            description={t('account:idpv_withdraw_option_description')}
                            testID="idpv-withdraw-verification-selector"
                            onPress={handleIdpvSelection}
                        />
                    </Box>
                </MaxWidthWrapper>
            </ScrollView>
        </Screen>
    );
};
